import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format } from 'date-fns';

interface SavedGriefMapsProps {
  onBack: () => void;
  userId: string;
}

interface GriefMap {
  id: string;
  created_at: string;
  updated_at: string;
  is_draft: boolean;
  loss_type: string;
  physical_sensations: string | null;
  emotional_layers: string | null;
  mental_patterns: string | null;
  spiritual_questions: string | null;
  relational_impact: string | null;
  physical_intensity: number;
  emotional_intensity: number;
  mental_intensity: number;
  spiritual_intensity: number;
  relational_intensity: number;
}

export const SavedGriefMaps = ({ onBack, userId }: SavedGriefMapsProps) => {
  const [maps, setMaps] = useState<GriefMap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState<GriefMap | null>(null);

  useEffect(() => {
    loadMaps();
  }, [userId]);

  const loadMaps = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('grief_maps')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMaps(data || []);
    } catch (error) {
      console.error('Error loading grief maps:', error);
      toast.error('Failed to load saved maps');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (mapId: string) => {
    if (!confirm('Are you sure you want to delete this grief map?')) return;

    try {
      const { error } = await supabase
        .from('grief_maps')
        .delete()
        .eq('id', mapId);

      if (error) throw error;

      setMaps(maps.filter(m => m.id !== mapId));
      setSelectedMap(null);
      toast.success('Grief map deleted');
    } catch (error) {
      console.error('Error deleting grief map:', error);
      toast.error('Failed to delete map');
    }
  };

  const getIntensityColor = (intensity: number) => {
    if (intensity >= 8) return 'bg-red-500/20 text-red-700 dark:text-red-400';
    if (intensity >= 5) return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
    return 'bg-green-500/20 text-green-700 dark:text-green-400';
  };

  if (selectedMap) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setSelectedMap(null)}>
            ← Back to Saved Maps
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(selectedMap.id)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-light">{selectedMap.loss_type}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(selectedMap.created_at), 'PPP')}
                  </CardDescription>
                </div>
              </div>
              {selectedMap.is_draft && (
                <Badge variant="secondary">Draft</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Physical Dimension */}
            {selectedMap.physical_sensations && (
              <Card className="border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">🫀</span> Physical Dimension
                    <Badge className={`ml-auto ${getIntensityColor(selectedMap.physical_intensity)}`}>
                      {selectedMap.physical_intensity}/10
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed">{selectedMap.physical_sensations}</p>
                </CardContent>
              </Card>
            )}

            {/* Emotional Dimension */}
            {selectedMap.emotional_layers && (
              <Card className="border-soft-accent/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">💧</span> Emotional Dimension
                    <Badge className={`ml-auto ${getIntensityColor(selectedMap.emotional_intensity)}`}>
                      {selectedMap.emotional_intensity}/10
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed">{selectedMap.emotional_layers}</p>
                </CardContent>
              </Card>
            )}

            {/* Mental Dimension */}
            {selectedMap.mental_patterns && (
              <Card className="border-secondary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">🧠</span> Mental Dimension
                    <Badge className={`ml-auto ${getIntensityColor(selectedMap.mental_intensity)}`}>
                      {selectedMap.mental_intensity}/10
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed">{selectedMap.mental_patterns}</p>
                </CardContent>
              </Card>
            )}

            {/* Spiritual Dimension */}
            {selectedMap.spiritual_questions && (
              <Card className="border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">✨</span> Spiritual Dimension
                    <Badge className={`ml-auto ${getIntensityColor(selectedMap.spiritual_intensity)}`}>
                      {selectedMap.spiritual_intensity}/10
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed">{selectedMap.spiritual_questions}</p>
                </CardContent>
              </Card>
            )}

            {/* Relational Dimension */}
            {selectedMap.relational_impact && (
              <Card className="border-soft-accent/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">🤝</span> Relational Dimension
                    <Badge className={`ml-auto ${getIntensityColor(selectedMap.relational_intensity)}`}>
                      {selectedMap.relational_intensity}/10
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed">{selectedMap.relational_impact}</p>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Your Saved Grief Maps</CardTitle>
              <CardDescription>
                Review and revisit your grief mapping journey
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-4">Loading your maps...</p>
            </div>
          ) : maps.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-base text-foreground mb-2">No grief maps saved yet</p>
              <p className="text-sm text-muted-foreground">
                Create your first grief map to begin tracking your reflection journey
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {maps.map((map) => (
                <Card 
                  key={map.id}
                  className="hover:shadow-therapy transition-all duration-300 cursor-pointer border-border/50"
                  onClick={() => setSelectedMap(map)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-medium text-foreground">{map.loss_type}</p>
                          {map.is_draft && (
                            <Badge variant="secondary" className="text-xs">Draft</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(map.created_at), 'PPP')}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge className={`text-xs ${getIntensityColor(map.physical_intensity)}`}>
                            Physical: {map.physical_intensity}/10
                          </Badge>
                          <Badge className={`text-xs ${getIntensityColor(map.emotional_intensity)}`}>
                            Emotional: {map.emotional_intensity}/10
                          </Badge>
                          <Badge className={`text-xs ${getIntensityColor(map.mental_intensity)}`}>
                            Mental: {map.mental_intensity}/10
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};